import React, { useLayoutEffect } from "react";
import { resolveAssetUrl } from "@/lib/api";
import { getLetterhead } from "@/lib/letterheads";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { fmtLongDate } from "@/lib/letterTemplates";

// A4 company letter on the business letterhead.
// mode="screen": header/footer are pinned to the top/bottom of one tall sheet (used in the editor preview).
// mode="print":  header/footer are position:fixed so the browser repeats them on every printed page.
export const HEADER_H = 46; // mm
export const FOOTER_H = 32; // mm

export const LETTER_CSS = `
.lt-sheet, .lt-sheet * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.lt-sheet { font-family: 'Montserrat', Arial, Helvetica, sans-serif; color: #222; background: #fff; width: 210mm; min-height: 297mm; position: relative; overflow: hidden; }
.lt-sheet.lt-print { overflow: visible; min-height: 0; }
.lt-body p { margin: 0 0 3mm; }
.lt-body h1 { font-size: 1.5em; margin: 0 0 3mm; }
.lt-body h2 { font-size: 1.25em; margin: 0 0 3mm; }
.lt-body h3, .lt-body h4 { font-size: 1.1em; margin: 0 0 2mm; }
.lt-body ol, .lt-body ul { margin: 0 0 3mm; padding-left: 7mm; }
.lt-body li { margin-bottom: 1.6mm; }
.lt-body table { border-collapse: collapse; width: 100%; margin: 2mm 0 4mm; }
.lt-body td, .lt-body th { border: 0.3mm solid #8c8c8c; padding: 1.5mm 2mm; vertical-align: top; }
.lt-body th { background: #f1ece0; text-align: left; }
.lt-body blockquote { margin: 0 0 3mm 6mm; padding-left: 3mm; border-left: 0.6mm solid #C49A22; }
.lt-editable { outline: none; min-height: 60mm; }
.lt-editable:focus { background: rgba(196,154,34,0.05); }
.lt-keep { break-inside: avoid; page-break-inside: avoid; }
`;

const PinIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg>
);
const PhoneIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" /></svg>
);
const MailIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg>
);
const GlobeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></svg>
);

// Very faint "D + roof" watermark (approximation of the DanRock mark)
const Watermark = ({ mode }) => (
  <div style={{ position: mode === "print" ? "fixed" : "absolute", left: "108mm", top: "132mm", width: "98mm", zIndex: 0, pointerEvents: "none" }}>
    <svg viewBox="0 0 200 204" width="100%">
      <path fill="#F6F2E9" fillRule="evenodd" d="M0 0 H98 C160 0 196 40 196 98 C196 126 186 148 170 164 H0 Z M44 36 V128 H128 C140 118 152 108 152 96 C152 60 130 36 92 36 Z" />
      <path fill="#F6F2E9" stroke="#FFFFFF" strokeWidth="9" paintOrder="stroke" d="M0 204 L110 98 L200 204 H166 L110 146 L46 204 Z" />
    </svg>
  </div>
);

function Header({ lh, mode }) {
  const pos = mode === "print" ? "fixed" : "absolute";
  return (
    <div style={{ position: pos, top: 0, left: 0, right: 0, height: `${HEADER_H}mm`, zIndex: 2, background: "transparent" }}>
      {lh.logo ? (
        <div style={{ position: "absolute", left: "15mm", top: "13mm", width: "60mm" }}>
          <img src={lh.logo} alt="" style={{ width: "100%", display: "block" }} />
        </div>
      ) : (
        <div style={{ position: "absolute", left: "15mm", top: "15mm", width: "95mm", fontFamily: "Georgia, serif", fontWeight: 700, fontSize: "19pt", color: lh.accent, lineHeight: 1.15 }}>
          {lh.companyName}
        </div>
      )}
      {lh.logo && <div style={{ position: "absolute", left: "118mm", top: "16mm", width: "0.3mm", height: "18mm", background: "#8a8a8a" }} />}
      {lh.logo && (
        <div style={{ position: "absolute", left: "127mm", top: "15.5mm", right: "7mm", lineHeight: 1.45 }}>
          <div style={{ fontWeight: 700, fontSize: "10.5pt", color: "#111" }}>{lh.companyName}</div>
          {lh.tagline && <div style={{ fontSize: "9.5pt", color: "#222" }}>{lh.tagline}</div>}
          {lh.cin && <div style={{ fontSize: "9.5pt", color: "#222" }}>{lh.cin}</div>}
        </div>
      )}
      <div style={{ position: "absolute", left: "6mm", right: "7mm", top: "44mm", height: "0.7mm", background: lh.accent }} />
    </div>
  );
}

function Footer({ lh, mode }) {
  const pos = mode === "print" ? "fixed" : "absolute";
  return (
    <div style={{ position: pos, bottom: 0, left: 0, right: 0, height: `${FOOTER_H}mm`, zIndex: 2 }}>
      <div style={{ position: "absolute", left: "87mm", right: 0, top: "5mm", bottom: 0, background: "#ECECEC" }} />
      <div style={{ position: "absolute", left: 0, top: 0, width: "103.5mm", height: "100%", background: lh.accent, clipPath: "polygon(0 0, 87mm 0, 103.5mm 100%, 0 100%)" }} />
      <div style={{ position: "absolute", left: "10mm", top: "7.5mm", width: "74mm", color: "#fff", display: "flex", gap: "3mm" }}>
        <div style={{ paddingTop: "0.8mm" }}><PinIcon /></div>
        <div style={{ lineHeight: 1.4 }}>
          <div style={{ fontWeight: 700, fontSize: "9.5pt" }}>Registered Office:</div>
          {lh.addressLines.map((l, i) => (<div key={i} style={{ fontSize: "8.5pt" }}>{l}</div>))}
        </div>
      </div>
      <div style={{ position: "absolute", left: "108mm", right: "6mm", top: "9mm", fontSize: "9pt", color: "#222", lineHeight: 1.3 }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          {lh.phone && (<span style={{ display: "inline-flex", alignItems: "center", gap: "2mm" }}><PhoneIcon />{lh.phone}</span>)}
          {lh.phone && lh.email && <span style={{ display: "inline-block", width: "0.3mm", height: "5mm", background: "#555", margin: "0 4mm" }} />}
          {lh.email && (<span style={{ display: "inline-flex", alignItems: "center", gap: "2mm" }}><MailIcon />{lh.email}</span>)}
        </div>
        {lh.website && (
          <div style={{ display: "flex", alignItems: "center", gap: "2mm", marginTop: "2.6mm" }}><GlobeIcon />{lh.website}</div>
        )}
      </div>
    </div>
  );
}

// Body that the user edits directly on the page (uncontrolled; HTML is pushed up via onInput)
function EditableBody({ html, resetKey, bodyRef, onInput }) {
  useLayoutEffect(() => {
    if (bodyRef.current) bodyRef.current.innerHTML = sanitizeHtml(html);
    // eslint-disable-next-line
  }, [resetKey]);
  return (
    <div
      ref={bodyRef}
      className="lt-editable"
      contentEditable
      suppressContentEditableWarning
      onInput={onInput}
      spellCheck
      data-testid="letter-body-editor"
    />
  );
}

export default function LetterDocument({ letter, profile, mode = "screen", editable = false, bodyRef, onBodyInput, bodyResetKey = 0 }) {
  const lh = getLetterhead(profile);
  const isPrint = mode === "print";
  const sigUrl = letter.show_signature_image && profile?.signature_url ? resolveAssetUrl(profile.signature_url) : "";
  const mode_ = letter.address_mode || "to";
  const addressLines = (letter.recipient_address || "").split("\n").map((s) => s.trim()).filter(Boolean);

  const content = (
    <div className="lt-body" style={{ fontSize: `${letter.font_size || 10.5}pt`, lineHeight: 1.6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6mm", fontSize: "0.95em" }}>
        <div>{letter.ref_no ? <>Ref: <b>{letter.ref_no}</b></> : ""}</div>
        <div>{letter.date ? <>Date: <b>{fmtLongDate(letter.date)}</b></> : ""}</div>
      </div>

      {letter.heading && (
        <div style={{ textAlign: "center", margin: "2mm 0 7mm" }}>
          <div style={{ fontWeight: 700, letterSpacing: "0.14em", fontSize: "1.25em", color: "#111" }}>{letter.heading}</div>
          <div style={{ width: "26mm", height: "0.7mm", background: lh.accent, margin: "2mm auto 0" }} />
        </div>
      )}

      {mode_ === "twimc" && (
        <p style={{ textAlign: "center", fontWeight: 700, margin: "0 0 6mm", letterSpacing: "0.08em" }}>TO WHOM IT MAY CONCERN</p>
      )}
      {mode_ === "to" && (letter.recipient_name || letter.recipient_designation || addressLines.length > 0) && (
        <div style={{ marginBottom: "5mm" }}>
          <div>To,</div>
          {letter.recipient_name && <div style={{ fontWeight: 700 }}>{letter.recipient_name}</div>}
          {letter.recipient_designation && <div>{letter.recipient_designation}</div>}
          {addressLines.map((l, i) => (<div key={i}>{l}</div>))}
        </div>
      )}

      {letter.subject && (
        <p style={{ fontWeight: 700, margin: "0 0 5mm" }}>Subject: {letter.subject}</p>
      )}

      {editable ? (
        <EditableBody html={letter.body_html} resetKey={bodyResetKey} bodyRef={bodyRef} onInput={onBodyInput} />
      ) : (
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(letter.body_html) }} />
      )}

      <div className="lt-keep" style={{ marginTop: "10mm" }}>
        <p style={{ margin: "0 0 1mm" }}>Yours faithfully,</p>
        {lh.signatureCompany && <p style={{ margin: 0, fontWeight: 700 }}>{lh.signatureCompany}</p>}
        {sigUrl ? (
          <img src={sigUrl} alt="" style={{ height: "17mm", display: "block", margin: "2mm 0" }} />
        ) : (
          <div style={{ height: "17mm" }} />
        )}
        {letter.signatory_name && <p style={{ margin: 0, fontWeight: 700 }}>{letter.signatory_name}</p>}
        {letter.signatory_designation && <p style={{ margin: 0 }}>{letter.signatory_designation}</p>}
      </div>

      {letter.show_acceptance && (
        <div className="lt-keep" style={{ marginTop: "12mm", borderTop: "0.3mm dashed #999", paddingTop: "4mm" }}>
          <p style={{ margin: "0 0 2mm", fontWeight: 700 }}>{letter.template === "warning" ? "Acknowledgement" : "Acceptance"}</p>
          <p style={{ margin: 0 }}>
            I, {letter.recipient_name ? <b>{letter.recipient_name}</b> : "__________________"},{" "}
            {letter.template === "warning"
              ? "acknowledge that I have received and read this letter."
              : "have read and understood the above terms and conditions and accept them."}
          </p>
          <p style={{ margin: "12mm 0 0" }}>Signature: ____________________ &nbsp;&nbsp;&nbsp;&nbsp; Date: ____________________</p>
        </div>
      )}
    </div>
  );

  return (
    <div className={`lt-sheet ${isPrint ? "lt-print" : ""}`} id="letter-sheet">
      <style>{LETTER_CSS}</style>
      {lh.key === "danrock" && <Watermark mode={mode} />}
      <Header lh={lh} mode={mode} />
      <Footer lh={lh} mode={mode} />
      {isPrint ? (
        <table style={{ width: "100%", borderCollapse: "collapse", position: "relative", zIndex: 1 }}>
          <thead><tr><td style={{ height: `${HEADER_H + 6}mm`, padding: 0 }} /></tr></thead>
          <tfoot><tr><td style={{ height: `${FOOTER_H + 8}mm`, padding: 0 }} /></tr></tfoot>
          <tbody><tr><td style={{ padding: "0 18mm", verticalAlign: "top" }}>{content}</td></tr></tbody>
        </table>
      ) : (
        <div style={{ position: "relative", zIndex: 1, minHeight: "297mm", padding: `${HEADER_H + 6}mm 18mm ${FOOTER_H + 8}mm` }}>
          {content}
        </div>
      )}
    </div>
  );
}
